import { useContext, useEffect, useMemo, useState, useRef } from 'react'
import { UIContext } from 'Context/UI'
import { useNavigate, useParams } from 'react-router-dom'
import { useProductLineStore } from 'Store/ProductLine'
import { useProductionStore } from 'Store/Production'
import Swal from 'sweetalert2'
import { shallow } from 'zustand/shallow'
import { generateImageUrl } from 'Utils/generateImageUrl'

const ViewModel = () => {
	const navigate = useNavigate()
	const { id } = useParams()
	const { setCustomContentToolbar, setShowContentToolbar } = useContext(UIContext)
	const isFixed = id !== undefined
	const productCategoryRef = useRef<any>(null)

	const [currentActiveProductLine, setCurrentActiveProductLine] = useState(0)
	const [currentActiveProductCategory, setCurrentActiveProductCategory] = useState(0)
	const [isLoadingProductLine, setIsLoadingProductLine] = useState(false)
	const [isLoadingProductCategory, setIsLoadingProductCategory] = useState(false)

	const { selectedWithChildren, setSelectedWithChildren, getOneWithChildren } = useProductLineStore(
		state => ({
			selectedWithChildren: state.selectedWithChildren,
			setSelectedWithChildren: state.setSelectedWithChildren,
			getOneWithChildren: state.getOneWithChildren,
		}),
		shallow
	)
	const { data, getAll, clearProductLineState } = useProductLineStore(
		state => ({
			data: state.data,
			getAll: state.getAll,
			clearProductLineState: state.clearState,
		}),
		shallow
	)
	const {
		productLines,
		productCategories,
		getProductLines,
		getProductCategories,
		clearProductionState,
	} = useProductionStore(
		state => ({
			productLines: state.productLines,
			productCategories: state.productCategories,
			getProductLines: state.getProductLines,
			getProductCategories: state.getProductCategories,
			clearProductionState: state.clearState,
		}),
		shallow
	)
	const productLineData = useMemo(() => {
		return data
			.filter((d: any) => d.parentId === null)
			.map((d: any) => {
				const finded = productLines.find((p: any) => p.id === d.id)

				return {
					...d,
					featurePicturePath: d.featurePicturePath
						? generateImageUrl(d.featurePicturePath)
						: undefined,
					disabled: !!finded,
					clickable: !!!finded,
				}
			})
	}, [productLines, data])

	const productCategoryData = useMemo(() => {
		if (selectedWithChildren?.child) {
			return selectedWithChildren?.child.map((d: any) => {
				const finded = productCategories.find((p: any) => p.id === d.id)

				return {
					...d,
					featurePicturePath: d.featurePicturePath
						? generateImageUrl(d.featurePicturePath)
						: undefined,
					disabled: finded,
					clickable: finded === undefined,
				}
			})
		} else {
			return []
		}
	}, [selectedWithChildren, productCategories])

	const changeProductLineActive = (idx: number) => {
		if (!isFixed) setCurrentActiveProductLine(idx)
	}

	const changeProductCategoryActive = (idx: number) => {
		if (!isFixed) setCurrentActiveProductCategory(idx)
	}

	const fetchData = async () => {
		setIsLoadingProductLine(true)

		getProductLines().then(({ success, data }) => {
			if (!success) {
				Swal.fire({
					icon: 'error',
					title: 'ดึงข้อมูลไม่สำเร็จ',
					text: data,
					confirmButtonText: 'ปิด',
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
					},
				}).then(() => {
					navigate('/general/production')
				})
			}

			// Product Line Masterdata
			getAll().then(({ success, data }) => {
				if (!success) {
					Swal.fire({
						icon: 'error',
						title: 'ดึงข้อมูลไม่สำเร็จ',
						text: data,
						confirmButtonText: 'ปิด',
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
					}).then(() => {
						navigate('/general/production')
					})
				} else {
					setIsLoadingProductCategory(true)

					if (data.length > 0) {
						const findedIdx = data.findIndex((d: any) => d.id === parseInt(id ?? ''))
						if (isFixed) setCurrentActiveProductLine(findedIdx)

						getProductCategories(parseInt(id ?? data[0].id)).then(
							({ success, data: dataProductCategories }) => {
								if (!success) {
									Swal.fire({
										icon: 'error',
										title: 'ดึงข้อมูลไม่สำเร็จ',
										text: dataProductCategories,
										confirmButtonText: 'ปิด',
										customClass: {
											confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
										},
									}).then(() => {
										navigate('/general/production')
									})
								} else {
									getOneWithChildren(isFixed ? data?.[findedIdx].id : data[0].id).then(
										({ success, data: dataGetOne }) => {
											if (!success) {
												Swal.fire({
													icon: 'error',
													title: 'ดึงข้อมูลไม่สำเร็จ',
													text: dataGetOne,
													confirmButtonText: 'ปิด',
													customClass: {
														confirmButton:
															'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
													},
												}).then(() => {
													navigate('/general/production')
												})
											} else {
												setIsLoadingProductCategory(false)
												setIsLoadingProductLine(false)
											}
										}
									)
								}
							}
						)
					}
				}
			})
		})
	}

	const fetchProductCategory = () => {
		const pl = productLineData.find((_, idx) => idx === currentActiveProductLine)

		if (pl) {
			setIsLoadingProductCategory(true)
			getProductCategories(parseInt(pl.id)).then(({ success, data: dataProductCategories }) => {
				if (!success) {
					Swal.fire({
						icon: 'error',
						title: 'ดึงข้อมูลไม่สำเร็จ',
						text: dataProductCategories,
						confirmButtonText: 'ปิด',
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
					}).then(() => {
						navigate('/general/production')
					})
				} else {
					getOneWithChildren(parseInt(pl.id)).then(({ success, data }) => {
						if (!success) {
							Swal.fire({
								icon: 'error',
								title: 'ดึงข้อมูลไม่สำเร็จ',
								text: data,
								confirmButtonText: 'ปิด',
								customClass: {
									confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
								},
							}).then(() => {
								navigate('/general/production')
							})
						} else {
							setIsLoadingProductCategory(false)
						}
					})
				}
			})
		}
	}

	const selectProductLine = (id: number, hasChild: boolean) => {
		if (hasChild) {
			productCategoryRef.current?.scrollIntoView({ behavior: 'smooth' })
			setCurrentActiveProductLine(currentActiveProductLine)
		} else {
			navigate(`/general/production/${id}/properties`)
		}
	}

	const selectProductCategory = (id: number) => {
		navigate(`/general/production/${id}/properties`)
	}

	const clearState = () => {
		setSelectedWithChildren({})
		clearProductionState()
		clearProductLineState()
	}

	const forceScroll = () => {
		productCategoryRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		fetchProductCategory()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentActiveProductLine])

	// Toolbar
	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
			clearState()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		// Status
		isLoadingProductLine,
		isLoadingProductCategory,
		isFixed,
		// Data
		currentActiveProductLine,
		currentActiveProductCategory,
		productLineData,
		productCategoryData,
		// Function
		changeProductLineActive,
		changeProductCategoryActive,
		selectProductLine,
		selectProductCategory,
		productCategoryRef,
		forceScroll,
	}
}

export default ViewModel
